// Discriminated union so it's assignable to React's `Usable<T>` (which is
// `Context<T> | FulfilledReactPromise<T> | PendingReactPromise<T> | RejectedReactPromise<T>`).
// A wide `status: 'pending' | 'fulfilled' | 'rejected'` can't narrow to the
// single-literal variants React expects when passed to `use()`.
export type ReactPromise<T> =
  | (Promise<T> & { status: 'pending'; value?: undefined; reason?: undefined })
  | (Promise<T> & { status: 'fulfilled'; value: T; reason?: undefined })
  | (Promise<T> & { status: 'rejected'; value?: undefined; reason: Error })

export const ReactPromise = {
  wrap<T>(value: Promise<T> | T): ReactPromise<T> {
    const promise = Promise.resolve(value) as Promise<T>
    const reactPromise = promise as ReactPromise<T>
    reactPromise.status = 'pending'

    promise
      .then((result) => {
        if (result instanceof Error) {
          // useQuery catches errors and returns them as data
          throw result
        }

        reactPromise.status = 'fulfilled'
        reactPromise.value = result
        return result
      })
      .catch((error) => {
        reactPromise.status = 'rejected'
        reactPromise.reason =
          error instanceof Error ? error : new Error(String(error))
      })

    return reactPromise
  },

  resolve<T>(value: T): ReactPromise<T> {
    const promise = Promise.resolve(value) as ReactPromise<T>
    promise.status = 'fulfilled'
    promise.value = value
    return promise
  },

  reject(error: Error): ReactPromise<never> {
    // oxlint-disable-next-line eslint-plugin-promise/no-promise-in-callback -- factory method, not a callback
    const promise = Promise.reject(error) as ReactPromise<never>
    promise.status = 'rejected'
    promise.reason = error
    return promise
  },
}

export class ReactSuspenseCache {
  private cache = new Map<string, ReactPromise<unknown>>()

  put<T>(key: string, promise: ReactPromise<T>): void {
    this.cache.set(key, promise)
  }

  get<T>(key: string): ReactPromise<T> | undefined {
    return this.cache.get(key) as ReactPromise<T> | undefined
  }

  has(key: string): boolean {
    return this.cache.has(key)
  }

  delete(key: string): boolean {
    return this.cache.delete(key)
  }

  clear(): void {
    this.cache.clear()
  }
}
